import { Tooltip } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "./../../axios";
import "./AddReport.css";
import { toast } from "react-toastify";
import {
  CalendarDays,
  Clock,
  BookOpen,
  GraduationCap,
  Users,
  FileText,
  Timer,
  ChevronRight,
  Award,
  BarChart3,
} from "lucide-react";

function AddReport() {
  const navigate = useNavigate();
  const { state } = useLocation();
  let teacher_id = JSON.parse(localStorage.getItem("Teacher_id"));
  const subjectList = [];
  const [specialChar, setSpecialChar] = useState(false);
  const [specialChar1, setSpecialChar1] = useState(false);
  const [nameOfAS, setName] = useState("");
  const [nameofDescription, setNameofDescription] = useState("");
  const [dValid, setDValid] = useState(true);

  // ─── Assessment Types & Events from Masters API ─────────
  const [assessmentTypesList, setAssessmentTypesList] = useState([]);
  const [assessmentEventsList, setAssessmentEventsList] = useState([]);
  const [assessmentTypesLoading, setAssessmentTypesLoading] = useState(false);
  const [assessmentEventsLoading, setAssessmentEventsLoading] = useState(false);
  const [assessmentConfigs, setAssessmentConfigs] = useState([]);
  const [assessmentConfigsLoading, setAssessmentConfigsLoading] =
    useState(false);

  // ─── Grade Options ──────────
  const [gradeOptions, setGradeOptions] = useState([]);
  const [selectedGradeScaleId, setSelectedGradeScaleId] = useState(null);
  const [entryType, setEntryType] = useState(null);

  const today = new Date();
  const disablePastDates = (date) => {
    return date < today;
  };

  // Auto-set current date & time
  const getCurrentDate = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getCurrentTime = () => {
    const d = new Date();
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const [assessmentDetails, setAssessmentDetails] = useState({
    name: null,
    description: null,
    teacher_id: teacher_id,
    type_id: null,
    event_id: null,
    standard_id: {
      id: state?.standardId ?? "null",
      name: "",
    },
    section_id: {
      id: state?.sectionId ?? "null",
      name: "",
    },
    subject_id: {
      id: state?.subjectId ?? "null",
      name: "",
    },
    tags: [],
    status: null,
    totalMarks: null,
    total_duration: null,
    schedule_date: getCurrentDate(),
    schedule_time: getCurrentTime(),
    add_to_live_session: null,
    questions: [],
    entryType: null,
    gradeScaleId: null,
    gradeOptions: [],
  });

  const {
    standard_id,
    section_id,
    subject_id,
    name,
    description,
    type_id,
    event_id,
    total_duration,
    totalMarks,
    schedule_date,
    schedule_time,
  } = assessmentDetails;

  const [dropdownLists, setDropdownLists] = useState({
    standards: null,
    sections: null,
    assessmentTypes: null,
    subjects: null,
  });

  // ─── Auto-generate Name & Description ──────────
  // ─── Auto-generate Name & Description ──────────
  useEffect(() => {
    const stdName = assessmentDetails.standard_id?.name || "";
    const secName = assessmentDetails.section_id?.name || "";
    const subName = assessmentDetails.subject_id?.name || "";
    const eventName = assessmentDetails.event_id?.name || "";
    const typeName = assessmentDetails.type_id?.name || "";

    // Format: 3-A-MidtermExam-General(Theory)
    let autoName = "";
    if (stdName && secName && eventName && typeName) {
      autoName = `${stdName}-${secName}-${eventName}-${typeName}`;
    } else if (stdName && secName && subName) {
      autoName = `${subName} - ${stdName} ${secName}`;
    }

    if (autoName) {
      const autoDesc = subName
        ? `Assessment for ${subName} in ${stdName} - ${secName}${
            eventName ? ` | Event: ${eventName}` : ""
          }${typeName ? ` | Type: ${typeName}` : ""}`
        : `Assessment for ${stdName} - ${secName}`;

      setName(autoName);
      setNameofDescription(autoDesc);
      setAssessmentDetails((prev) => ({
        ...prev,
        name: autoName,
        description: autoDesc,
      }));
    }
  }, [
    assessmentDetails.standard_id?.name,
    assessmentDetails.section_id?.name,
    assessmentDetails.subject_id?.name,
    assessmentDetails.event_id?.name,
    assessmentDetails.type_id?.name,
  ]);

  // ─── Fetch Assessment Events by Standard & Section ──────────
  const getAssessmentEventsByStandardSection = async (stdId, secId) => {
    if (!stdId || !secId || stdId === "null" || secId === "null") return;
    setAssessmentEventsLoading(true);
    try {
      let res = await axios.get(
        `/api/v1/performance/assessment-events?schoolId=${school_id}`,
      );
      const data = res?.data?.data || res?.data || [];
      const activeEvents = data.filter(
        (ev) => ev.status === true || ev.status === "Active",
      );
      setAssessmentEventsList(activeEvents);
    } catch (error) {
      console.log("Error fetching assessment events:", error);
      setAssessmentEventsList([]);
    } finally {
      setAssessmentEventsLoading(false);
    }
  };

  // ─── Fetch Assessment Configurations ──────────
  const getAssessmentConfigurations = async (stdId, secId, eventId) => {
    if (
      !stdId ||
      !secId ||
      !eventId ||
      stdId === "null" ||
      secId === "null" ||
      eventId === "null"
    ) {
      setAssessmentConfigs([]);
      setAssessmentTypesList([]);
      setSelectedGradeScaleId(null);
      setEntryType(null);
      setGradeOptions([]);
      return;
    }
    setAssessmentConfigsLoading(true);
    setAssessmentTypesLoading(true);
    try {
      let res = await axios.get(
        `/api/v1/performance/assessment-configurations?standardId=${stdId}&sectionId=${secId}&assessmentEventId=${eventId}`,
      );
      const configs = res?.data?.data || res?.data || [];
      setAssessmentConfigs(configs);

      if (configs && configs.length > 0) {
        const firstConfig = configs[0];
        if (firstConfig.entryType) {
          setEntryType(firstConfig.entryType);
          setAssessmentDetails((prev) => ({
            ...prev,
            entryType: firstConfig.entryType,
          }));
        }
        if (firstConfig.gradeScaleId) {
          const gradeScaleId =
            firstConfig.gradeScaleId?._id || firstConfig.gradeScaleId;
          setSelectedGradeScaleId(gradeScaleId);
          setAssessmentDetails((prev) => ({
            ...prev,
            gradeScaleId: gradeScaleId,
          }));
        }
      }
    } catch (error) {
      console.log("Error fetching assessment configurations:", error);
      setAssessmentConfigs([]);
      setAssessmentTypesList([]);
    } finally {
      setAssessmentConfigsLoading(false);
      setAssessmentTypesLoading(false);
    }
  };

  const getAssessmentType = async () => {
    try {
      let res = await axios.get(
        `/api/v1/assessment/assessmentType/get/school/${school_id}`,
      );
      res &&
        setDropdownLists((prev) => ({
          ...prev,
          assessmentTypes: res.data.data,
        }));
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getAssessmentType();
  }, []);

  const { standards, sections, assessmentTypes, subjects } = dropdownLists;
  const CTRole_Information = JSON.parse(
    localStorage.getItem("role_as_class_teacher"),
  );
  const CoRole_Information = JSON.parse(
    localStorage.getItem("role_as_co_ordinator"),
  );
  const general_role = JSON.parse(localStorage.getItem("general_role"));
  let school_id = JSON.parse(localStorage.getItem("schoolId"));

  let sectionArr = [];
  let standardArr = [];
  const dropDownHelper = () => {
    function addUniqueObjectToArraystand(obj) {
      if (
        !standardArr.some(
          (item) => JSON.stringify(item) === JSON.stringify(obj),
        )
      ) {
        standardArr.push(obj);
      }
    }
    general_role?.map((obj) => addUniqueObjectToArraystand(obj.standard_id));
    CTRole_Information?.map((obj) =>
      addUniqueObjectToArraystand(obj.standard_id),
    );
    CoRole_Information?.map((obj) =>
      addUniqueObjectToArraystand(obj.standard_id),
    );
  };

  dropDownHelper();

  const handleDropDownSec = () => {
    function addUniqueObjectToArraysect(obj) {
      if (
        !sectionArr.some((item) => JSON.stringify(item) === JSON.stringify(obj))
      ) {
        sectionArr.push(obj);
      }
    }
    general_role?.map((obj) => {
      if (obj?.standard_id?.id === assessmentDetails?.standard_id?.id) {
        addUniqueObjectToArraysect(obj.section_id);
      }
    });
    CTRole_Information?.map((obj) => {
      if (obj?.standard_id?.id === assessmentDetails?.standard_id?.id) {
        addUniqueObjectToArraysect(obj.section_id);
      }
    });
    CoRole_Information?.map((obj) => {
      if (obj?.standard_id?.id === assessmentDetails?.standard_id?.id) {
        addUniqueObjectToArraysect(obj.section_id);
      }
    });
  };
  handleDropDownSec();

  const getSubjects = async (standard_id, section_id) => {
    let arrSubject = [];
    try {
      let res = await axios.get(
        `/api/v1/school/standardId/${standard_id}/sectionId/${section_id}/getSubjects`,
      );
      const data = res?.data?.data;

      const subjectSource =
        data?.subjectConfigurations?.length > 0
          ? data.subjectConfigurations.map((item) => item.subject)
          : data.subjects;

      subjectSource.forEach((obj) => {
        arrSubject.push(obj);
      });
    } catch (error) {
      console.log(error);
    }
    return arrSubject;
  };

  useEffect(() => {
    (async () => {
      let subjects = [];
      const stdId = assessmentDetails.standard_id?.id;
      const secId = assessmentDetails.section_id?.id;

      if (
        ((CTRole_Information && CTRole_Information.length > 0) ||
          (CoRole_Information && CoRole_Information.length > 0)) &&
        stdId !== "null" &&
        secId !== "null"
      ) {
        subjects = await getSubjects(stdId, secId);
      }
      if (CTRole_Information && CTRole_Information.length > 0) {
        CTRole_Information?.forEach(async (obj) => {
          if (obj.standard_id.id === stdId && obj.section_id.id === secId) {
            if (subjects.length > 0) {
              for (const subject of subjects) {
                if (!subjectList.some((i) => i.id === subject.id)) {
                  subjectList.push({ id: subject.id, name: subject.name });
                }
              }
            }
          }
        });
      }
      if (CoRole_Information && CoRole_Information.length > 0) {
        CoRole_Information?.forEach(async (obj) => {
          if (obj.standard_id.id === stdId && obj.section_id.id === secId) {
            if (subjects.length > 0) {
              for (const subject of subjects) {
                if (!subjectList.some((i) => i.id === subject.id)) {
                  subjectList.push({ id: subject.id, name: subject.name });
                }
              }
            }
          }
        });
      }
      if (general_role && general_role.length > 0) {
        general_role?.forEach((obj) => {
          let subjects = obj.subjects_id;
          if (obj.standard_id.id === stdId && obj.section_id.id === secId) {
            for (const subject of subjects) {
              if (!subjectList.some((i) => i.id === subject.id)) {
                subjectList.push({ id: subject.id, name: subject.name });
              }
            }
          }
        });
      }

      subjectList.length > 0 &&
        setDropdownLists((prev) => ({ ...prev, subjects: subjectList }));

      if (stdId && secId && stdId !== "null" && secId !== "null") {
        getAssessmentEventsByStandardSection(stdId, secId);
      } else {
        setAssessmentEventsList([]);
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentDetails.standard_id, assessmentDetails.section_id]);

  useEffect(() => {
    const draft = localStorage.getItem("addReportDraft");
    if (draft) {
      const parsed = JSON.parse(draft);
      setAssessmentDetails(parsed);
      if (parsed.entryType) setEntryType(parsed.entryType);
      if (parsed.gradeScaleId) setSelectedGradeScaleId(parsed.gradeScaleId);
      if (parsed.gradeOptions?.length) setGradeOptions(parsed.gradeOptions);
      if (parsed.name) setName(parsed.name);
      if (parsed.description) setNameofDescription(parsed.description);
    }
  }, []);

  // ─── Fetch configurations when event changes ──────────
  useEffect(() => {
    const stdId = assessmentDetails.standard_id?.id;
    const secId = assessmentDetails.section_id?.id;
    const eventId = assessmentDetails.event_id?.id;

    if (
      stdId &&
      secId &&
      eventId &&
      stdId !== "null" &&
      secId !== "null" &&
      eventId !== "null"
    ) {
      getAssessmentConfigurations(stdId, secId, eventId);
    } else {
      setAssessmentConfigs([]);
      setAssessmentTypesList([]);
      setSelectedGradeScaleId(null);
      setEntryType(null);
      setGradeOptions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentDetails.event_id]);

  // ─── Filter types when subject changes ──────────
  useEffect(() => {
    const subjectId = assessmentDetails?.subject_id?.id;

    if (!subjectId || subjectId === "null") {
      setAssessmentTypesList([]);
      return;
    }

    const filteredConfigs = assessmentConfigs.filter(
      (config) =>
        config?.subjectId?._id === subjectId ||
        config?.subjectId?.id === subjectId,
    );

    const uniqueTypes = [];
    const typeIds = new Set();

    filteredConfigs.forEach((config) => {
      const type = config?.assessmentTypeId;

      if (type && !typeIds.has(type._id || type.id)) {
        typeIds.add(type._id || type.id);
        uniqueTypes.push(type);
      }
    });

    setAssessmentTypesList(uniqueTypes);

    if (filteredConfigs && filteredConfigs.length > 0) {
      const firstConfig = filteredConfigs[0];
      if (firstConfig.entryType) {
        setEntryType(firstConfig.entryType);
        setAssessmentDetails((prev) => ({
          ...prev,
          entryType: firstConfig.entryType,
        }));
      }
      if (firstConfig.gradeScaleId) {
        const gradeScaleId =
          firstConfig.gradeScaleId?._id || firstConfig.gradeScaleId;
        setSelectedGradeScaleId(gradeScaleId);
        setAssessmentDetails((prev) => ({
          ...prev,
          gradeScaleId: gradeScaleId,
        }));
      }
    }
  }, [assessmentDetails?.subject_id?.id, assessmentConfigs]);

  useEffect(() => {
    if (
      standards &&
      sections &&
      state?.standardId !== undefined &&
      state?.sectionId !== undefined
    ) {
      let standard =
        standards &&
        standards.length &&
        standards.filter((standard) => standard.id == state.standardId);
      let section =
        sections &&
        sections.length &&
        sections.filter((section) => section.id == state.sectionId);
      setAssessmentDetails({
        ...assessmentDetails,
        standard_id: {
          ...assessmentDetails.standard_id,
          name: standard[0].name,
        },
        section_id: {
          ...assessmentDetails.section_id,
          name: section[0].name,
        },
      });
    }
  }, [standards, sections]);

  useEffect(() => {
    if (subjects && subjects.length > 0 && state?.subjectId !== undefined) {
      let subject = subjects.filter((subject) => subject.id == state.subjectId);
      setAssessmentDetails({
        ...assessmentDetails,
        subject_id: {
          ...assessmentDetails.subject_id,
          name: subject[0].name,
        },
      });
    }
  }, [subjects]);

  const selectedConfig = assessmentConfigs.find(
    (config) =>
      (config?.subjectId?._id === assessmentDetails.subject_id?.id ||
        config?.subjectId?.id === assessmentDetails.subject_id?.id) &&
      (config?.assessmentTypeId?._id === assessmentDetails.type_id?.id ||
        config?.assessmentTypeId?.id === assessmentDetails.type_id?.id),
  );

  // ─── Update based on selectedConfig ──────────
  useEffect(() => {
    if (!selectedConfig) return;

    if (selectedConfig.entryType === "Marks") {
      setAssessmentDetails((prev) => ({
        ...prev,
        totalMarks: selectedConfig.maxMarks,
        entryType: "Marks",
        total_duration: 60,
        gradeScaleId: null,

        gradeOptions: [],
      }));
      setGradeOptions([]);
      setEntryType("Marks");
    } else if (selectedConfig.entryType === "Grade") {
      const gradeScaleId =
        selectedConfig.gradeScaleId?._id || selectedConfig.gradeScaleId;
      const grades = selectedConfig.gradeScaleId?.grades || [];

      setGradeOptions(grades);
      setEntryType("Grade");
      setSelectedGradeScaleId(gradeScaleId);

      setAssessmentDetails((prev) => ({
        ...prev,
        totalMarks: null,
        entryType: "Grade",
        total_duration: 60,
        gradeScaleId: gradeScaleId,
        gradeOptions: grades,
      }));
    }
  }, [
    assessmentDetails.subject_id?.id,
    assessmentDetails.type_id?.id,
    assessmentConfigs,
  ]);

  console.log("📊 Entry Type:", entryType);
  console.log("📊 Grade Options:", gradeOptions);

  // ─── isDisabled ──────────
  const isDisabled = () => {
    const baseCheck =
      !standard_id ||
      subject_id?.id === "null" ||
      !section_id ||
      !type_id ||
      !event_id;

    if (baseCheck) return true;

    const currentEntryType = assessmentDetails?.entryType || entryType;

    if (currentEntryType === "Marks") {
      return !totalMarks;
    }

    return false;
  };

  // ─── handleNextClick ──────────
  const handleNextClick = () => {
    if (!isDisabled()) {
      const payload = {
        ...assessmentDetails,
        entryType: entryType,
        gradeScaleId: selectedGradeScaleId,
        gradeOptions: gradeOptions,
      };

      // 👈 Draft save karo taki back aane pe mile
      localStorage.setItem("addReportDraft", JSON.stringify(payload));

      navigate("/offline-submission-report", {
        state: {
          assessmentDetails: payload,
          dropdownLists,
          state,
        },
      });
    }
  };

  return (
    <div
      className="w-100"
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)",
        padding: "24px",
      }}
    >
      {/* ─── Header ────────── */}
      <div
        className="d-flex align-items-center justify-content-between mb-4"
        style={{ cursor: "pointer" }}
        onClick={() => navigate("/offline-performance/academic")}
      >
        <div>
          <h2
            style={{
              fontSize: "24px",
              fontWeight: "700",
              color: "#1a1a2e",
              margin: 0,
              letterSpacing: "-0.5px",
            }}
          >
            Student Marks Entry
          </h2>
          <p
            style={{
              fontSize: "13px",
              color: "#6b7280",
              margin: "4px 0 0 0",
            }}
          >
            Dashboard / Performance / Student Marks Entry (Offline)
          </p>
        </div>
      </div>

      {/* ─── Main Form Card ────────── */}
      <div
        style={{
          background: "white",
          borderRadius: "20px",
          boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
          padding: "32px",
        }}
      >
        {/* Section Title */}
        <div className="d-flex align-items-center gap-2 mb-4">
          <div
            style={{
              width: "36px",
              height: "36px",
              background: "linear-gradient(135deg, #f77f00 0%, #ff9f43 100%)",
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FileText size={18} color="white" />
          </div>
          <div>
            <h4
              style={{
                fontSize: "16px",
                fontWeight: "700",
                color: "#1a1a2e",
                margin: 0,
              }}
            >
              Assessment Configuration
            </h4>
            <p
              style={{
                fontSize: "12px",
                color: "#9ca3af",
                margin: "2px 0 0 0",
              }}
            >
              Select class, section, event and other details
            </p>
          </div>
        </div>

        {/* ─── ROW 1: Class, Section, Event ────────── */}
        <div className="row g-3 mb-4">
          <div className="col-md-4">
            <label
              style={{
                fontSize: "12px",
                fontWeight: "600",
                color: "#4b5563",
                marginBottom: "8px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <GraduationCap size={14} color="#f77f00" />
              Class
            </label>
            <select
              disabled={state?.defaultData !== undefined ? true : false}
              className="form-select"
              value={assessmentDetails?.standard_id?.id}
              onChange={(e) =>
                setAssessmentDetails({
                  ...assessmentDetails,
                  standard_id: {
                    id: e.target.value,
                    name: e.target.options[e.target.selectedIndex].getAttribute(
                      "name",
                    ),
                  },
                  subject_id: {
                    id: "null",
                    name: "null",
                  },
                  section_id: {
                    id: "null",
                    name: "null",
                  },
                  event_id: null,
                  type_id: null,
                })
              }
              style={{
                borderRadius: "12px",
                border: "2px solid #e5e7eb",
                padding: "12px 16px",
                fontSize: "14px",
                height: "48px",
                transition: "all 0.2s",
                cursor:
                  state?.defaultData !== undefined ? "not-allowed" : "pointer",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#f77f00")}
              onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
            >
              <option value="null" name="null">
                Select class
              </option>
              {standardArr?.length > 0 &&
                standardArr
                  .sort((a, b) => {
                    const aNum = Number(a.name);
                    const bNum = Number(b.name);
                    const bothNumeric = !isNaN(aNum) && !isNaN(bNum);
                    if (bothNumeric) return aNum - bNum;
                    return a.name.localeCompare(b.name);
                  })
                  ?.map((standard, index) => (
                    <option
                      key={index}
                      value={standard?.id}
                      name={standard?.name}
                    >
                      {standard?.name}
                    </option>
                  ))}
            </select>
          </div>

          <div className="col-md-4">
            <label
              style={{
                fontSize: "12px",
                fontWeight: "600",
                color: "#4b5563",
                marginBottom: "8px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <Users size={14} color="#f77f00" />
              Section
            </label>
            <select
              className="form-select"
              disabled={state?.defaultData !== undefined ? true : false}
              value={assessmentDetails?.section_id?.id}
              onChange={(e) =>
                setAssessmentDetails({
                  ...assessmentDetails,
                  section_id: {
                    id: e.target.value,
                    name: e.target.options[e.target.selectedIndex].getAttribute(
                      "name",
                    ),
                  },
                  subject_id: {
                    id: "null",
                    name: "null",
                  },
                  event_id: null,
                  type_id: null,
                })
              }
              style={{
                borderRadius: "12px",
                border: "2px solid #e5e7eb",
                padding: "12px 16px",
                fontSize: "14px",
                height: "48px",
                transition: "all 0.2s",
                cursor:
                  state?.defaultData !== undefined ? "not-allowed" : "pointer",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#f77f00")}
              onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
            >
              <option value="null" name="null">
                Select Section
              </option>
              {standard_id?.id !== "null" &&
                sectionArr?.length > 0 &&
                sectionArr
                  ?.slice()
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((section, index) => (
                    <option key={index} value={section.id} name={section.name}>
                      {section.name}
                    </option>
                  ))}
            </select>
          </div>

          <div className="col-md-4">
            <label
              style={{
                fontSize: "12px",
                fontWeight: "600",
                color: "#4b5563",
                marginBottom: "8px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <CalendarDays size={14} color="#f77f00" />
              Assessment Event
            </label>
            <select
              className="form-select"
              value={assessmentDetails?.event_id?.id || "null"}
              disabled={
                assessmentEventsLoading ||
                !assessmentDetails.standard_id?.id ||
                !assessmentDetails.section_id?.id ||
                assessmentDetails.standard_id?.id === "null" ||
                assessmentDetails.section_id?.id === "null"
              }
              onChange={(e) => {
                const selectedEvent = assessmentEventsList.find(
                  (ev) => (ev.id || ev._id) === e.target.value,
                );
                setAssessmentDetails({
                  ...assessmentDetails,
                  event_id: {
                    id: e.target.value,
                    name: e.target.options[e.target.selectedIndex].getAttribute(
                      "name",
                    ),
                  },
                  subject_id: { id: "null", name: "null" },
                  type_id: null,
                });
              }}
              style={{
                borderRadius: "12px",
                border: "2px solid #e5e7eb",
                padding: "12px 16px",
                fontSize: "14px",
                height: "48px",
                transition: "all 0.2s",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#f77f00")}
              onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
            >
              <option value="null" name="null">
                {assessmentEventsLoading
                  ? "Loading..."
                  : "Select Assessment Event"}
              </option>
              {assessmentEventsList?.length > 0 &&
                assessmentEventsList.map((event, index) => (
                  <option
                    key={index}
                    value={event?.id || event?._id}
                    name={event?.name}
                  >
                    {event?.name}
                    {event?.category ? ` (${event.category})` : ""}
                  </option>
                ))}
            </select>
          </div>
        </div>

        {/* Divider */}
        <div
          style={{
            height: "1px",
            background:
              "linear-gradient(90deg, transparent, #e5e7eb, transparent)",
            margin: "24px 0",
          }}
        />

        {/* ─── ROW 2: Subject, Type, Duration, Total Marks ────────── */}
        <div className="row g-3 mb-4">
          <div className="col-md-3">
            <label
              style={{
                fontSize: "12px",
                fontWeight: "600",
                color: "#4b5563",
                marginBottom: "8px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <BookOpen size={14} color="#f77f00" />
              Subject
            </label>
            <select
              className="form-select"
              disabled={
                state?.defaultData !== undefined
                  ? true
                  : false || !assessmentDetails?.event_id?.id
              }
              value={assessmentDetails?.subject_id?.id}
              onChange={(e) =>
                setAssessmentDetails({
                  ...assessmentDetails,
                  subject_id: {
                    id: e.target.value,
                    name: e.target.options[e.target.selectedIndex].getAttribute(
                      "name",
                    ),
                  },
                  type_id: null,
                })
              }
              style={{
                borderRadius: "12px",
                border: "2px solid #e5e7eb",
                padding: "12px 16px",
                fontSize: "14px",
                height: "48px",
                transition: "all 0.2s",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#f77f00")}
              onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
            >
              <option value="null" name="null">
                Select subject
              </option>
              {standard_id?.id !== "null" &&
                section_id?.id !== "null" &&
                subjects?.length > 0 &&
                subjects?.map((subject, index) => {
                  const hasConfig = assessmentConfigs.some(
                    (cfg) =>
                      cfg.subjectId?._id === subject.id ||
                      cfg.subjectId?.id === subject.id,
                  );
                  if (
                    !assessmentDetails.event_id?.id ||
                    assessmentDetails.event_id?.id === "null" ||
                    hasConfig
                  ) {
                    return (
                      <option
                        key={index}
                        value={subject.id}
                        name={subject.name}
                      >
                        {subject.name}
                      </option>
                    );
                  }
                  return null;
                })}
            </select>
          </div>

          <div className="col-md-3">
            <label
              style={{
                fontSize: "12px",
                fontWeight: "600",
                color: "#4b5563",
                marginBottom: "8px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <Award size={14} color="#f77f00" />
              Assessment Type
            </label>
            <select
              className="form-select"
              value={assessmentDetails?.type_id?.id || "null"}
              disabled={
                assessmentConfigsLoading ||
                assessmentTypesLoading ||
                !assessmentDetails.event_id?.id ||
                assessmentDetails.event_id?.id === "null" ||
                !assessmentDetails.subject_id?.id ||
                assessmentDetails.subject_id?.id === "null"
              }
              onChange={(e) =>
                setAssessmentDetails({
                  ...assessmentDetails,
                  type_id: {
                    id: e.target.value,
                    name: e.target.options[e.target.selectedIndex].getAttribute(
                      "name",
                    ),
                  },
                })
              }
              style={{
                borderRadius: "12px",
                border: "2px solid #e5e7eb",
                padding: "12px 16px",
                fontSize: "14px",
                height: "48px",
                transition: "all 0.2s",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#f77f00")}
              onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
            >
              <option value="null" name="null">
                {assessmentConfigsLoading || assessmentTypesLoading
                  ? "Loading..."
                  : assessmentDetails.event_id?.id &&
                      assessmentDetails.event_id?.id !== "null"
                    ? "Select Assessment Type"
                    : "Select Event First"}
              </option>
              {assessmentTypesList?.length > 0 &&
                assessmentTypesList.map((type, index) => (
                  <option
                    key={index}
                    value={type?._id || type?.id}
                    name={type?.name}
                  >
                    {type?.name}
                  </option>
                ))}
            </select>
          </div>
          {entryType === "Marks" && (
            <div className="col-md-3">
              <label
                style={{
                  fontSize: "12px",
                  fontWeight: "600",
                  color: "#4b5563",
                  marginBottom: "8px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <BarChart3 size={14} color="#f77f00" />
                Total Marks
              </label>
              <input
                className="form-control"
                placeholder="e.g. 100"
                value={assessmentDetails.totalMarks || ""}
                onChange={(e) => {
                  let value = e.target.value;
                  if (/^\d*$/.test(value) || value === "") {
                    const maxValue = 100;
                    if (value === "" || parseInt(value) <= maxValue) {
                      setAssessmentDetails({
                        ...assessmentDetails,
                        totalMarks: value,
                      });
                    }
                  }
                }}
                style={{
                  borderRadius: "12px",
                  border: "2px solid #e5e7eb",
                  padding: "12px 16px",
                  fontSize: "14px",
                  height: "48px",
                  transition: "all 0.2s",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#f77f00")}
                onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                type="number"
                min="0"
                max="100"
                maxLength={4}
              />
            </div>
          )}

          {entryType === "Grade" && (
            <div className="col-md-3">
              <label
                style={{
                  fontSize: "12px",
                  fontWeight: "600",
                  color: "#4b5563",
                  marginBottom: "8px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <Award size={14} color="#f77f00" />
                Grade Scale
              </label>
              <div
                className="form-control d-flex align-items-center justify-content-center"
                style={{
                  borderRadius: "12px",
                  border: "2px solid #e5e7eb",
                  height: "48px",
                  background:
                    "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                  color: "#6c757d",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                <Award size={16} style={{ marginRight: "8px" }} />
                Grade Scale Applied
              </div>
            </div>
          )}
        </div>

        {/* ─── Next Button ────────── */}
        <div className="d-flex justify-content-end mt-5">
          <button
            onClick={handleNextClick}
            disabled={isDisabled()}
            style={{
              background: isDisabled()
                ? "#e5e7eb"
                : "linear-gradient(135deg, #f77f00 0%, #ff9f43 100%)",
              color: isDisabled() ? "#9ca3af" : "white",
              width: "140px",
              height: "48px",
              borderRadius: "12px",
              border: "none",
              fontSize: "15px",
              fontWeight: "600",
              cursor: isDisabled() ? "not-allowed" : "pointer",
              boxShadow: isDisabled()
                ? "none"
                : "0 4px 16px rgba(247,127,0,0.3)",
              transition: "all 0.2s",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
            onMouseEnter={(e) => {
              if (!isDisabled()) {
                e.target.style.transform = "translateY(-2px)";
                e.target.style.boxShadow = "0 6px 20px rgba(247,127,0,0.4)";
              }
            }}
            onMouseLeave={(e) => {
              if (!isDisabled()) {
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "0 4px 16px rgba(247,127,0,0.3)";
              }
            }}
          >
            Next
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddReport;
